<script>
/**
 * Transcrições de reunião do cliente em foco.
 *
 * Cada linha é um LINK de verdade (`<a target="_blank">`), não um `@click` com
 * `window.open`: o link nativo abre com clique do meio, com Ctrl e pelo menu de
 * contexto, e o leitor de tela o anuncia como link. Um botão perderia os três.
 *
 * ⚠️ O destino é o deep-link que a tela de histórico já entende —
 * `crm.transcricoes.history?transcricao=<id>`, o mesmo do CTA do e-mail de
 * resumo (ver `deepLinkTranscriptId` naquela view). NÃO existe rota de detalhe
 * por path; inventar `/transcricoes/:id` daria 404.
 *
 * Abre em ABA NOVA de propósito: sair da `/ia` no meio de uma conversa custa o
 * contexto do chat, e a transcrição é consulta de apoio.
 *
 * ⚠️ DUAS linhas por item, no máximo: título com a nota à direita, e embaixo a
 * data com o sentimento. A tela do CRM mostra também a descrição e os botões de
 * ouvir e resumir — aqui não cabem, e a linha é um atalho para lá, não uma
 * cópia dela.
 */
import {
  sentimentMeta,
  normalizeSentimento,
} from '@modules/crm/constants/Sentiment'
import { contadorDeLista } from '@modules/aawz-intelligence-v2/utils/cliente-em-foco/contador-de-lista'

const ROTULO = 'Transcrições'
const TAMANHO_ICONE = 14
const TAMANHO_CHEVRON = 12
const ICONE = 'microphone'
const ROTA_HISTORICO = 'crm.transcricoes.history'
// Dois (era 3 até 14/09/2026): a seção divide a última linha do painel com os
// contratos e é a coluna mais alta dela; o painel tem de caber no piso de 660px
// do bloco superior — conta no `<style>` do `ia-cliente-painel.vue`. O total
// vai para o contador.
const MAX_ITENS = 2
// Uma linha só: a seção costuma ter poucos itens, e um esqueleto mais alto que
// o conteúdo final faz a coluna encolher quando o dado chega.
const LINHAS_PENDENTES = [{ chave: 'p1' }]
const CAIXA_NOME = { width: '70%', height: '13px' }
const CAIXA_META = { width: '46%', height: '11px' }
const TITULO_PADRAO = 'Reunião sem título'
const SEPARADOR_META = ' · '
// Uma casa decimal, como o CRM mostra ("8.5").
const CASAS_DA_NOTA = 1

export default {
  name: 'IaClienteTranscricoes',
  props: {
    // `value.transcripts` de `apiCrmTranscricoes.listar`, cru.
    transcricoes: { type: Array, default: () => [] },
    carregando: { type: Boolean, default: false },
    navegavel: { type: Boolean, default: false },
  },
  data() {
    return { LINHAS_PENDENTES, CAIXA_NOME, CAIXA_META, TAMANHO_ICONE, TAMANHO_CHEVRON, ICONE }
  },
  computed: {
    rotulo() {
      return ROTULO
    },
    itens() {
      return (this.transcricoes || [])
        .slice(0, MAX_ITENS)
        .map((item, indice) => ({
          chave: 'call:' + (item.id || item.meeting_id || indice),
          // Reunião sem título cai no id do encontro, como na tabela do CRM.
          nome: item.title || item.meeting_id || TITULO_PADRAO,
          meta: this.metaDaReuniao(item),
          nota: this.notaDaReuniao(item),
          corDoSentimento: this.corDoSentimento(item),
          href: this.linkDaTranscricao(item),
        }))
    },
    // "2 de 5" — o total é exato, porque a lista inteira chega por prop.
    // Fórmula única em `contadorDeLista`.
    contador() {
      return contadorDeLista({ exibidos: this.itens.length, total: (this.transcricoes || []).length })
    },
    vazio() {
      return !this.carregando && !this.itens.length
    },
  },
  methods: {
    aoClicar() {
      if (!this.navegavel) return
      this.$emit('abrir')
    },
    /**
     * "18/08/2026 16:00 · Positivo".
     *
     * ⚠️ O backend JÁ manda a data formatada, não em ISO — passá-la por
     * `formatDate` devolvia vazio, e foi por isso que ela não aparecia. Aqui a
     * hora FICA: numa lista de reuniões do mesmo dia, é ela que as distingue.
     *
     * O sentimento só entra quando existe: ele depende do processamento da IA e
     * chega nulo em reunião recém-encerrada.
     */
    metaDaReuniao(item) {
      const sentimento = normalizeSentimento(item.sentimento_atual)
      const partes = [String(item.start_time || '')]
      if (sentimento) partes.push(sentimentMeta(sentimento).label)
      return partes.filter(Boolean).join(SEPARADOR_META)
    },
    // `0` é nota válida e `null` é ausência — daí o teste por tipo, e não por
    // veracidade: `!item.sentimento_score` esconderia a nota zero.
    notaDaReuniao(item) {
      const nota = item.sentimento_score
      if (typeof nota !== 'number' || !isFinite(nota)) return ''
      return nota.toFixed(CASAS_DA_NOTA)
    },
    /**
     * Cor do ponto ao lado da nota. Vem do mapa do CRM (`SENTIMENT_META`), não
     * de um mapa próprio: é o mesmo verde/âmbar/vermelho que a tela de
     * transcrições usa, e duas paletas para o mesmo conceito divergiriam.
     */
    corDoSentimento(item) {
      const sentimento = normalizeSentimento(item.sentimento_atual)
      return sentimentMeta(sentimento).cssVar
    },
    /**
     * ⚠️ Sem `id` não há link — a linha vira texto simples em vez de um `<a>`
     * que leva a lugar nenhum. Transcrição em processamento pode chegar assim.
     */
    linkDaTranscricao(item) {
      if (!item.id) return ''
      const rota = this.$router.resolve({
        name: ROTA_HISTORICO,
        query: { transcricao: item.id },
      })
      return (rota && rota.href) || ''
    },
  },
}
</script>

<template>
  <div class="ia-cliente-transcricoes">
    <component
      :is="navegavel ? 'button' : 'div'"
      class="ia-cliente-transcricoes__rotulo ia-texto-selo"
      :type="navegavel ? 'button' : null"
      @click="aoClicar"
    >
      <span>{{ rotulo }}</span>
      <!-- ⚠️ `arrow-left` aponta para a DIREITA — ver a nota no `ia-insight-card`. -->
      <icon-svg
        v-if="navegavel"
        name="arrow-left"
        :size="TAMANHO_CHEVRON"
        color="var(--neutral-600)"
      />
      <span v-if="!carregando" class="ia-sidebar-contador ml-auto" data-test="ia-cliente-transcricoes-contador">
        {{ contador }}
      </span>
    </component>

    <ul v-if="carregando" class="ia-cliente-transcricoes__lista">
      <li v-for="linha in LINHAS_PENDENTES" :key="linha.chave">
        <icon-svg
          :name="ICONE"
          :size="TAMANHO_ICONE"
          color="var(--neutral-300)"
        />
        <!-- ⚠️ O `span` com `flex: 1` é o que dá largura à caixa: a do esqueleto
             é em PORCENTAGEM e, solta no `li`, resolvia contra zero. -->
        <span class="ia-cliente-transcricoes__texto">
          <BaseLoadingSkeleton v-bind="CAIXA_NOME" />
          <BaseLoadingSkeleton v-bind="CAIXA_META" />
        </span>
      </li>
    </ul>

    <p v-else-if="vazio" class="ia-cliente-transcricoes__vazio ia-texto-auxiliar">
      Nenhuma transcrição
    </p>

    <ul v-else class="ia-cliente-transcricoes__lista">
      <li v-for="item in itens" :key="item.chave">
        <icon-svg
          :name="ICONE"
          :size="TAMANHO_ICONE"
          color="var(--neutral-700)"
        />
        <component
          :is="item.href ? 'a' : 'span'"
          class="ia-cliente-transcricoes__texto"
          :class="{ 'ia-cliente-transcricoes__texto--link': item.href }"
          :href="item.href || null"
          :target="item.href ? '_blank' : null"
          :rel="item.href ? 'noopener noreferrer' : null"
        >
          <span class="ia-cliente-transcricoes__linha">
            <span class="ia-cliente-transcricoes__nome ia-texto-auxiliar">{{ item.nome }}</span>
            <span v-if="item.nota" class="ia-cliente-transcricoes__nota ia-texto-auxiliar">
              <span
                class="ia-cliente-transcricoes__ponto"
                :style="{ background: item.corDoSentimento }"
              />
              {{ item.nota }}
            </span>
          </span>
          <span v-if="item.meta" class="ia-cliente-transcricoes__meta ia-texto-auxiliar">
            {{ item.meta }}
          </span>
        </component>
      </li>
    </ul>
  </div>
</template>

<style lang="sass" scoped>
.ia-cliente-transcricoes
  display: flex
  flex-direction: column
  gap: 10px

// Mesma régua do `.ia-insight__rotulo`.
.ia-cliente-transcricoes__rotulo
  color: var(--neutral-600)
  display: flex
  align-items: center
  gap: 4px
  width: 100%
  padding: 0
  background: transparent
  border: 0
  letter-spacing: 0.06em
  text-transform: uppercase

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.ia-cliente-transcricoes__lista
  display: flex
  flex-direction: column
  gap: 10px
  margin: 0
  padding: 0
  list-style: none

  li
    display: flex
    align-items: flex-start
    gap: 8px
    min-width: 0

.ia-cliente-transcricoes__texto
  display: flex
  flex-direction: column
  gap: 2px
  flex: 1
  min-width: 0
  text-decoration: none

// Sublinhado só no hover: três linhas sublinhadas em repouso viram uma parede de
// traços numa coluna estreita.
.ia-cliente-transcricoes__texto--link:hover .ia-cliente-transcricoes__nome
  text-decoration: underline

.ia-cliente-transcricoes__texto--link:focus-visible
  outline: 2px solid var(--primary)
  outline-offset: 2px
  border-radius: 2px

// Título à esquerda, nota encostada à direita.
.ia-cliente-transcricoes__linha
  display: flex
  align-items: center
  gap: 8px
  min-width: 0

// ⚠️ 12px e `font-weight` EXPLÍCITO — a mesma régua do texto de estado vazio
// ("Nenhum contrato") logo abaixo. Sem o peso declarado, o `<a>` herdava o do
// contexto e as linhas saíam mais encorpadas que as das seções vizinhas.
.ia-cliente-transcricoes__nome
  color: var(--neutral-900)
  flex: 1
  min-width: 0
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

// A nota não encolhe: é o título que cede espaço, porque é ele que varia de
// tamanho.
.ia-cliente-transcricoes__nota
  color: var(--neutral-800)
  display: flex
  align-items: center
  gap: 4px
  flex-shrink: 0
  font-variant-numeric: tabular-nums

// A cor do ponto é o SENTIMENTO; o número é a nota. Separar os dois deixa a
// linha responder duas perguntas no espaço de uma.
.ia-cliente-transcricoes__ponto
  width: 6px
  height: 6px
  border-radius: 50%

.ia-cliente-transcricoes__meta
  color: var(--neutral-600)
  font-variant-numeric: tabular-nums
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.ia-cliente-transcricoes__vazio
  color: var(--neutral-600)
  margin: 0
</style>
